// ============================================================================
// Bedroom range expansion
// ----------------------------------------------------------------------------
// The `property_bedrooms_range` field stores a compact range like "Studio - 2"
// or "1 - 3". For prose ("...featuring brand new Studio, 1, and 2 bedroom
// floor plans") we want the full enumeration of every bedroom count in that
// range, not the raw endpoints.
//
// `expandBedroomRange` parses the two endpoints, walks every integer level
// between them, and renders a grammatical list ("Studio and 1", "Studio, 1,
// and 2"). Level 0 (or the literal "Studio"/"Std") renders as "Studio".
//
// Anything it can't confidently parse (unexpected format, descending range, or
// an implausibly wide span) falls back to the trimmed raw string, so a weird
// value degrades to showing the range as authored rather than to empty text.
// ============================================================================

/** Largest span we'll enumerate; guards against a malformed "1 - 40" ballooning. */
const MAX_SPAN = 6;

/**
 * Parse a single endpoint token to a bedroom level. "Studio"/"Std"/"0" → 0,
 * "2 Bedrooms" → 2. Returns null for anything non-numeric.
 */
function bedTokenToLevel(token: string): number | null {
  const t = token.trim().toLowerCase();
  if (!t) return null;
  if (t.startsWith('studio') || t === 'std') return 0;
  const n = parseInt(t, 10); // parseInt stops at the first non-digit ("2 bedrooms" → 2)
  return Number.isNaN(n) ? null : n;
}

/** Render a bedroom level for display: 0 → "Studio", otherwise the number. */
function levelToLabel(level: number): string {
  return level <= 0 ? 'Studio' : String(level);
}

/** Join items with commas and a final "and" (Oxford comma for 3+). */
function joinWithAnd(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Expand a compact bedroom range into a grammatical enumeration.
 *
 * @example expandBedroomRange('Studio - 2') // → 'Studio, 1, and 2'
 * @example expandBedroomRange('1 - 3')      // → '1, 2, and 3'
 * @example expandBedroomRange('2')          // → '2'
 */
export function expandBedroomRange(range: string | null | undefined): string {
  if (!range) return '';
  const raw = range.trim();
  // Split on dash variants or the word "to" (e.g. "Studio to 2").
  const parts = raw
    .split(/\s*(?:–|—|-|\bto\b)\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return raw;

  const first = bedTokenToLevel(parts[0]);
  const last = bedTokenToLevel(parts[parts.length - 1]);

  // Fall back to the raw string on anything we can't confidently enumerate.
  if (first === null || last === null || last < first || last - first > MAX_SPAN) {
    return raw;
  }

  const labels: string[] = [];
  for (let level = first; level <= last; level++) labels.push(levelToLabel(level));
  return joinWithAnd(labels);
}
